import { Avatar, Box, Button, Flex, Grid, Heading, Icon, Text } from '@chakra-ui/react';
import { useResources } from 'datx-jsonapi-react';
import NextLink from 'next/link';
import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowIcon from '@/assets/icons/ic-arrow-tilted.svg';

import { LabeledLectureCard } from '@/components/event/LabeledLectureCard';
import { formatInstitutionLine } from '@/components/Speaker/utils';
import { LectureCardSkeleton } from '@/components/ui/shared/lecture/LectureCard/LectureCardSkeleton/LectureCardSkeleton';
import { GeneralMarkdown } from '@/components/ui/shared/markdowns/GeneralMarkdown/GeneralMarkdown';
import { ContactFormModalPage } from '@/components/ui/shared/profile/ProfilePopup/components/ContactFormModalPage';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { queryProfileInstitutions } from '@/queries/profile-instututions';
import { queryLecturesWithFilters } from '@/queries/thin-lectures';
import { ThinProfile } from '@/resources/ThinProfile';

interface IProfileListModalPage {
  profile: ThinProfile;
}

export const SingleProfileModalPage: FC<IProfileListModalPage> = ({ profile }) => {
  const { thinProfile: myThinProfile } = useSession();
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const { data: institutions } = useResources(queryProfileInstitutions(profile.id));
  const { t } = useTranslation();
  const { eventId } = useQueryParams<{
    eventId: string;
    eventSessionId: string;
    sessionId: string;
  }>({
    eventId: StringParam,
    eventSessionId: StringParam,
    sessionId: StringParam,
  });

  const filters = {
    event_id: eventId,
    speaker_id: profile.id,
  };

  const { data = [], meta, isValidating } = useResources(queryLecturesWithFilters(100, 1, filters));

  const isMyProfile = myThinProfile?.id === profile.id;

  if (contactFormOpen) return <ContactFormModalPage profile={profile} closeForm={() => setContactFormOpen(false)} />;

  return (
    <Flex maxW={{ base: '336', md: '985px' }} w="full" flexDir="column">
      <Flex alignItems="center">
        <Box mr={{ base: 4, md: 8 }}>
          <Avatar
            bg="transparent"
            color="white"
            name={profile.fullName}
            src={profile.avatar}
            boxSize={{ base: '100px', md: '150px' }}
          />
        </Box>
        <Flex flexDir="column" alignSelf="flex-start" w="full">
          <Flex flexDir={{ base: 'column', lg: 'row' }} justifyContent="space-between" w="full">
            <Heading fontSize={{ base: 'xl', md: '3xl' }}>{profile.fullName}</Heading>
            {isMyProfile && (
              <NextLink key={profile.id} href="/my-account/profile">
                <Button alignSelf="flex-start" mt={2} size="sm" variant="solid" colorScheme="white" borderWidth={1}>
                  edit profile
                </Button>
              </NextLink>
            )}
          </Flex>
          <Text fontFamily="body" fontWeight="bold" mt={2}>
            {profile.title}
          </Text>

          {profile.emailPublic && profile.email && (
            <Button
              variant="solid"
              colorScheme="black"
              size="sm"
              alignSelf="flex-start"
              onClick={() => setContactFormOpen(true)}
              mt={2}
            >
              email
            </Button>
          )}
        </Flex>
      </Flex>
      <Flex flexDir="column" mt={4} maxH="250px" w="full" overflowY="auto">
        <GeneralMarkdown escapeHtml={true} source={profile?.shortBio} />
      </Flex>
      {institutions?.length > 1 && (
        <Flex mt={2} flexDirection="column">
          <Heading mb={2} fontSize="sm" color="gray.600">
            {t('publicSpeakerPage.otherInstitutionalAffiliations')}
          </Heading>
          <Flex flexDirection="column">
            {institutions?.slice(1).map((inst) => (
              <Text key={inst.id}>{formatInstitutionLine(inst)}</Text>
            ))}
          </Flex>
        </Flex>
      )}
      <NextLink target="_blank" key={profile.id} href={`/speakers/${profile.id}-${profile.slug}`}>
        <Button
          rightIcon={<Icon as={ArrowIcon} color="white" h={5} w={5} />}
          variant="solid"
          colorScheme="black"
          size="sm"
          alignSelf="flex-start"
          mt={4}
        >
          view full profile
        </Button>
      </NextLink>
      <Fragment>
        {(isValidating || data.length > 0) && (
          <Text fontWeight="bold" fontSize="md" mb={2} mt={4}>
            PRESENTATIONS AT THIS EVENT:
          </Text>
        )}
        <Grid
          maxH="500px"
          overflowY="auto"
          overflowX="hidden"
          gap={2}
          gridTemplateColumns={{
            base: '1fr',
            md: 'repeat(2, 1fr)',
            lg: `repeat(3, 1fr)`,
          }}
        >
          {data.map((lecture) => (
            <LabeledLectureCard profilesEnabled={false} key={lecture.id} lecture={lecture} eventId={eventId} />
          ))}

          {isValidating && (
            <Fragment>
              {Array.from({ length: 3 }, (_, i) => (
                <LectureCardSkeleton key={i} width="300px" minH="400px" />
              ))}
            </Fragment>
          )}
        </Grid>
      </Fragment>
    </Flex>
  );
};
